import {
  BookOpen, BookOpenCheck, Building2, CircleHelp, ClipboardCheck, Clock, FileSpreadsheet, FileText, FolderOpen, LifeBuoy, ListTodo,
  Plus, Scale, ScanSearch, Send, Shield, ShieldCheck, TriangleAlert, UserCheck, Wrench,
} from "lucide-react";
import type { FrameworkId } from "../context/FrameworksContext";
import type { ClientNavCounts } from "./NavCountsContext";
import {
  ESG_FW_IDS, FW_COLOR, FW_LABEL, frameworkPages, isCertificationFramework, orderedFrameworks,
} from "./frameworkMeta";
import type { NavItem, NavSection, SetupChecklist } from "./types";
import { applyRoleFocus } from "./roleFocus";
import { CERTIFIABLE_FRAMEWORKS } from "../lib/frameworkCatalogue";

// ── Client shell navigation ───────────────────────────────────────────────
// Builds docs/NAVIGATION.md section 4. The sidebar is organised by task:
// Home; My standards (one row per standard, only the current one open); Laws &
// privacy (its own section, each register a row); Work — Documents, Risk &
// suppliers, Resilience, Improvement, Security & AI, Reports — each a collapsible
// group whose pages are its rows (with their own badges) and also tabs above the
// page (NavItem.tabs, nav/areas.ts); Settings and Help. The same builder feeds the
// client portal, the consultant read-through and (through its own builder) the
// Jersey edition.
//
// Three things keep the sidebar short without taking anything away:
//   - relevance: an optional register the organisation has not used, or a
//     regime that does not apply to it, is `deferred` - listed on More tools
//     and in the command palette, left out of the sidebar;
//   - tier: a page above the organisation's service tier is `lock`ed and
//     likewise listed on More tools, once, instead of a padlock row each;
//   - Guided mode (the default experience): the advanced registers are
//     deferred too until they hold something.
// Permission-gated items are `hidden`. None of this is access control: the API
// enforces tier and permission exactly as before.

export interface ClientAddons {
  tprm: boolean;
  doraMonitoring: boolean;
  legalObligations: boolean;
  esg: boolean;
  /** The auditor access add-on: auditor feedback below Full Service (backend lib/auditorAccess.ts). */
  auditorAccess: boolean;
  hardening: boolean;
}

export const NO_ADDONS: ClientAddons = {
  tprm: false, doraMonitoring: false, legalObligations: false, esg: false, auditorAccess: false, hardening: false,
};

/**
 * Scope of the Data Protection page (`/gdpr`), from GET /organizations/addons.
 * Law-driven rather than a paid toggle: the page opens for every data protection
 * law in the organisation's Operating Locations or implied by a gdpr / uk_gdpr
 * enrolment; `ropa` says whether any of those laws requires a record of processing.
 */
export interface DataProtectionScope {
  enabled: boolean;
  /** "GDPR", "UK GDPR", "GDPR & UK GDPR", "Jersey DPJL" or "Data Protection". */
  label: string;
  ropa: boolean;
}

export interface ClientNavInput {
  /** The organisation's service tier level, or null while it is still unknown (no lock affordance then). */
  tierLevel: number | null;
  addons: ClientAddons;
  enrolled: ReadonlySet<FrameworkId> | Iterable<FrameworkId>;
  pciSaqTypes: string[];
  permissions: string[];
  counts: ClientNavCounts | null;
  publicApiEnabled: boolean;
  frameworkScores: Array<{ id: FrameworkId; score: number | null }>;
  /** Null until /organizations/addons answers: the item then falls back to the gdpr / uk_gdpr enrolment. */
  dataProtection?: DataProtectionScope | null;
  /** Guided experience mode (the default): the short menu. Expert shows every page that applies. */
  guided?: boolean;
}

/** Minimum service tier per client feature (backend FEATURE_MIN_TIER where enforced). */
export const CLIENT_MIN_TIER = {
  complianceCheck: 3,
  executiveReport: 3,
  nextSteps: 3,
  policyRepository: 2,
  evidenceRepository: 2,
  exportEvidence: 2,
  /** Technical scope documents (backend /technical-scopes behind requireTier("documents")). */
  technicalScopes: 2,
  /** Customer security questionnaires (backend /customer-questionnaires behind requireTier("documents"); drafting "aiaudit"). */
  customerQuestionnaires: 2,
  /** Connected storage (documents sync, evidence import) comes with the Documents tier … */
  connections: 2,
  /** … the security connectors on the same page need Self-Service + AI (backend "integrations"). */
  securityConnectors: 3,
  hardening: 3,
  /** Response rooms and the offline crisis pack (backend "responseroom"). */
  responseRooms: 3,
  /** Mock audit rehearsals (backend /mock-audits behind requireTier("aiaudit")). */
  mockAudit: 3,
  /** Questions answered from the registers (backend /register-questions behind requireTier("aiaudit")). */
  askRegisters: 3,
  auditorFeedback: 5,
} as const;

export function tierLock(tierLevel: number | null, minTier: number, feature: string): NavItem["lock"] {
  // An unknown tier locks nothing: the row renders plainly until the profile
  // answers (the API enforces the tier regardless, and the gated pages show
  // their own TierLockModal on a 403), so nothing flashes and nothing shifts.
  if (tierLevel === null) return undefined;
  return tierLevel >= minTier ? undefined : { kind: "tier", minTier, feature };
}

/** "72%" for a scored framework, "n/a" for one that has no score, null while scores are unknown. */
export function frameworkChip(id: FrameworkId, scores: Array<{ id: FrameworkId; score: number | null }>): string | null {
  const entry = scores.find((s) => s.id === id);
  if (!entry) return null;
  return entry.score === null ? "n/a" : `${Math.round(entry.score)}%`;
}

/** Plain names for the framework sub-pages; the formal term stays in brackets for those who look for it. */
const PLAIN_PAGE_LABELS: Record<string, string> = {
  "Statement of Applicability": "What applies (SoA)",
};

function frameworkGroup(id: FrameworkId, pciSaqTypes: string[], scores: ClientNavInput["frameworkScores"]): NavItem {
  return {
    id: `fw:${id}`,
    label: FW_LABEL[id],
    dot: FW_COLOR[id],
    chip: frameworkChip(id, scores),
    // One row per standard; only the one being worked in opens (it opens itself when its page is current).
    defaultOpen: false,
    keywords: [id.replace(/_/g, " ")],
    children: frameworkPages(id, pciSaqTypes).map((page) => ({
      id: `fw:${id}:${page.to}`,
      label: PLAIN_PAGE_LABELS[page.label] ?? page.label,
      to: page.to,
      keywords: [FW_LABEL[id], page.label],
    })),
  };
}

function gdprLabel(enrolled: ReadonlySet<FrameworkId>): string {
  const eu = enrolled.has("gdpr");
  const uk = enrolled.has("uk_gdpr");
  if (eu && uk) return "GDPR & UK GDPR";
  if (uk) return "UK GDPR";
  return "GDPR";
}

/**
 * Whether a page is filed under More tools. `relevant`: the regime or add-on
 * applies at all. `advanced`: a register Guided mode keeps out of the way until
 * it is `used` (it holds rows, or has something needing attention).
 */
export function deferFor(guided: boolean, opts: { relevant?: boolean; advanced?: boolean; used?: boolean }): boolean {
  const { relevant = true, advanced = false, used = false } = opts;
  if (used) return false;
  if (!relevant) return true;
  return guided && advanced;
}

export function buildClientNav(input: ClientNavInput): NavSection[] {
  const { tierLevel, addons, pciSaqTypes, permissions, publicApiEnabled, frameworkScores, counts } = input;
  const guided = input.guided ?? false;
  const enrolled: ReadonlySet<FrameworkId> = input.enrolled instanceof Set
    ? (input.enrolled as ReadonlySet<FrameworkId>)
    : new Set(input.enrolled as Iterable<FrameworkId>);
  const has = (p: string) => permissions.includes(p);
  const isAdmin = has("org:admin");
  // The narrower permissions carved out of org:admin / compliance:run. The API expands
  // implied permissions, so an administrator holds every one of these too.
  const canReport = has("reports:generate");
  const canIntegrate = has("integrations:admin");
  const canBilling = has("billing:view");
  const canPeople = has("people:write");
  // The API only reports group:view for a user on a group's parent organisation.
  const groupParent = has("group:view");
  // Until the counts answer nothing counts as used: the optional registers stay
  // filed away rather than flashing into the sidebar and out again.
  const usage = counts?.usage;
  const used = (n: number | undefined) => (n ?? 0) > 0;
  const defer = (opts: { relevant?: boolean; advanced?: boolean; used?: boolean }) => deferFor(guided, opts);

  // ── Home ────────────────────────────────────────────────────────────────
  const home: NavSection = {
    id: "home",
    items: [
      {
        id: "home", label: "Home", icon: ListTodo, to: "/actions", badge: "actions",
        tabs: [
          { id: "actions", label: "What's next", to: "/actions", badge: "actions", description: "Everything that needs doing, most urgent first", keywords: ["home", "tasks", "to do", "my actions", "setup"] },
          { id: "dashboard", label: "Overview", to: "/dashboard", description: "Scores, gaps and recent activity at a glance", keywords: ["dashboard", "scores"] },
          { id: "guides", label: "Guides", to: "/guides", description: "Step-by-step: adopt your first policy, prepare for your audit", keywords: ["guided", "walkthrough", "how to", "steps", "audit ready"] },
          {
            id: "guidance", label: "AI advice", to: "/guidance",
            lock: tierLock(tierLevel, CLIENT_MIN_TIER.nextSteps, "AI advice"),
            deferred: defer({ advanced: true }),
            description: "Recommendations from the latest compliance check",
            keywords: ["next steps", "guidance", "recommendations", "continuous"],
          },
          { id: "analytics", label: "Progress", to: "/analytics", deferred: defer({ advanced: true }), description: "How your scores have moved over time", keywords: ["analytics", "trends", "score", "charts"] },
          { id: "schedule", label: "Calendar", to: "/schedule", description: "Reviews, audits and renewals coming up", keywords: ["review calendar", "audits", "reviews", "events"] },
          {
            // Every member's own list: policies to read and confirm, training to complete, approvals to decide.
            id: "my-tasks", label: "My tasks", to: "/my-tasks", badge: "myTasks",
            deferred: defer({ relevant: used(usage?.policyCampaigns) || used(usage?.trainingAssignments), used: (counts?.myTasks ?? 0) > 0 }),
            description: "Policies to read, training to complete and approvals waiting for you",
            keywords: ["policy acknowledgement", "attestation", "read and sign", "training", "approve", "tasks"],
          },
          { id: "group", label: "Group", to: "/group", hidden: !groupParent, description: "Every organisation in your group", keywords: ["group overview", "subsidiaries", "parent", "holding"] },
        ],
      },
    ],
  };

  // ── My standards ────────────────────────────────────────────────────────
  const ordered = orderedFrameworks(enrolled);
  const certificationIds = ordered.filter(isCertificationFramework);
  const standardItems: NavItem[] = certificationIds.map((id) => frameworkGroup(id, pciSaqTypes, frameworkScores));
  // The ESG add-on's standards sit with the others; the add-on is their entitlement.
  if (addons.esg) {
    for (const id of ordered.filter((f) => ESG_FW_IDS.has(f))) standardItems.push(frameworkGroup(id, pciSaqTypes, frameworkScores));
  }

  // Check & prepare leads the section: it acts on every standard below it.
  standardItems.unshift({
    id: "check", label: "Check & prepare", icon: Clock, badge: "runningCheck",
    tabs: [
      {
        id: "compliance", label: "Compliance check", to: "/compliance", badge: "runningCheck",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.complianceCheck, "AI Compliance Check"),
        description: "Have the AI assess your evidence against each standard",
        keywords: ["ai", "check", "run", "assessment"],
      },
      {
        id: "remediation-roadmap", label: "Gap roadmap", to: "/remediation-roadmap",
        description: "Your open gaps in the order to close them, dated to the audit",
        keywords: ["roadmap", "plan", "gaps", "remediation", "certification plan"],
      },
      { id: "compliance-questions", label: "About your business", to: "/compliance-questions", description: "The questions that tailor the platform to you", keywords: ["intake questions", "questionnaire", "intake", "answers"] },
      {
        id: "control-mapping", label: "Standards overlap", to: "/control-mapping",
        // Only meaningful once there is more than one standard to line up.
        deferred: defer({ relevant: certificationIds.length > 1 }),
        description: "Where one piece of work counts towards several standards",
        keywords: ["control mapping", "crosswalk", "mapping"],
      },
    ],
  });

  const gdprEnrolled = enrolled.has("gdpr") || enrolled.has("uk_gdpr");
  // The page is law-driven (every data protection law in scope, not only a GDPR
  // enrolment); until the server answers, the enrolment alone decides.
  const dpScope: DataProtectionScope = input.dataProtection ?? { enabled: gdprEnrolled, label: gdprLabel(enrolled), ropa: gdprEnrolled };

  if (isAdmin) {
    standardItems.push({ id: "add-standard", label: "Add a standard", to: "/my-services", icon: Plus, keywords: ["enrol", "framework", "standard"] });
  }
  const standards: NavSection = { id: "standards", label: "My standards", items: standardItems };

  // ── Laws & privacy ──────────────────────────────────────────────────────
  // Its own section: the laws that apply, the four data protection registers as rows of their
  // own (each is a tab of /gdpr, reached by its own path), where sensitive data sits and what goes
  // to a regulator. The registers appear only when a data protection law applies (the page is
  // law-driven, lib/dataProtectionModule.ts); the record of processing only where a law requires one.
  const dp = dpScope.enabled;
  const law: NavSection = {
    id: "law", label: "Laws & privacy",
    items: [
      { id: "legal", label: "Legal obligations", to: "/legal", icon: Scale, hidden: !addons.legalObligations && !dp, description: "The laws that apply to you and what each asks", keywords: ["law", "regulations", "nis2", "dora", "legal"] },
      { id: "gdpr-ropa", label: "Records of processing", to: "/gdpr/ropa", icon: FileSpreadsheet, hidden: !dp || !dpScope.ropa, description: `What personal data you process and why (${dpScope.label})`, keywords: ["ropa", "record of processing", "article 30", "data protection", "privacy"] },
      { id: "gdpr-requests", label: "Data subject requests", to: "/gdpr/dsr", icon: UserCheck, hidden: !dp, description: "Access, erasure and other requests, against the statutory deadline", keywords: ["dsar", "dsr", "subject access", "erasure", "rights", "privacy"] },
      { id: "gdpr-dpia", label: "Impact assessments", to: "/gdpr/dpia", icon: ClipboardCheck, hidden: !dp, description: "Data protection impact assessments", keywords: ["dpia", "impact assessment", "article 35", "privacy"] },
      { id: "gdpr-breaches", label: "Breaches", to: "/gdpr/breaches", icon: TriangleAlert, hidden: !dp, description: "Personal data breaches and who must be told", keywords: ["personal data breach", "article 33", "notification", "ico", "joic", "privacy"] },
      { id: "gdpr-agreements", label: "Agreements", to: "/gdpr/agreements", icon: FileText, hidden: !dp, description: "Processor agreements you hold as processor, joint controller arrangements, data sharing, transfer tools and transfer risk assessments", keywords: ["joint controller", "article 26", "data sharing agreement", "scc", "idta", "transfer risk assessment", "tra", "dpa", "privacy"] },
      {
        id: "data-protection-mapping", label: "Privacy by article", to: "/data-protection-mapping", icon: BookOpen, hidden: !addons.legalObligations,
        deferred: defer({ advanced: true }),
        description: "Each data protection article mapped to your controls",
        keywords: ["data protection mapping", "mapping", "articles"],
      },
      {
        id: "data-discovery", label: "Sensitive data", to: "/data-discovery", icon: ScanSearch, badge: "dataDiscovery",
        deferred: defer({ advanced: true, used: used(usage?.dataDiscovery) || (counts?.dataDiscovery ?? 0) > 0 }),
        description: "Where personal and sensitive data sits, from Purview, your cloud and your connected folders",
        keywords: ["data discovery", "dlp", "purview", "macie", "sensitive data", "personal data", "classification", "a.5.12"],
      },
      {
        id: "submissions", label: "Regulator returns", to: "/submissions", icon: Send, badge: "submissions",
        deferred: defer({ relevant: counts?.oesPossible ?? false, used: used(usage?.submissions) || (counts?.submissions ?? 0) > 0 }),
        description: "Registrations, notifications and returns to a regulator",
        keywords: ["regulatory submissions", "notification", "regulator", "authority", "return", "registration", "oes"],
      },
    ],
  };

  // ── Work ────────────────────────────────────────────────────────────────
  const documents: NavItem = {
    id: "documents-area", label: "Documents", icon: FolderOpen,
    tabs: [
      { id: "templates", label: "Write policies", to: "/templates", description: "Generate a policy tailored to your organisation", keywords: ["policy templates", "generate", "docx", "templates"] },
      {
        id: "policies", label: "Your policies", to: "/policies",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.policyRepository, "Policy Repository"),
        description: "Every policy you have generated or adopted",
        keywords: ["policy repository", "generated", "documents", "policies"],
      },
      {
        id: "documents", label: "Evidence", to: "/documents",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.evidenceRepository, "Evidence Repository"),
        description: "Files that show you do what your policies say",
        keywords: ["evidence repository", "files", "uploads", "evidence"],
      },
      {
        id: "export", label: "Audit pack", to: "/export",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.exportEvidence, "Export Evidence"),
        description: "Download everything an auditor will ask for",
        keywords: ["export evidence", "package", "zip", "auditor pack"],
      },
      {
        id: "policy-governance", label: "Policy sign-off", to: "/policy-governance", badge: "governance",
        deferred: defer({ advanced: true, used: used(usage?.policyCampaigns) || (counts?.governance ?? 0) > 0 }),
        description: "Management approval of each policy, and staff confirming they have read it",
        keywords: ["policy governance", "policy approval", "acknowledgement", "attestation", "distribution", "review", "5.2", "a.5.1"],
      },
      {
        // Time-limited departures from an adopted policy, each a risk acceptance (docs/POLICY-EXCEPTIONS.md).
        id: "policy-exceptions", label: "Policy exceptions", to: "/policy-exceptions",
        deferred: defer({ advanced: true, used: used(usage?.policyExceptions) }),
        description: "Time-limited departures from a policy, approved by someone else and accepted as a risk",
        keywords: ["policy exception", "exception register", "waiver", "dispensation", "deviation", "risk acceptance", "a.5.1", "a.5.36"],
      },
      {
        // Written scopes for work someone carries out on the organisation's systems (docs/TECHNICAL-SCOPES.md).
        id: "technical-scopes", label: "Technical scopes", to: "/technical-scopes",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.technicalScopes, "Technical scopes"),
        deferred: defer({ advanced: true, used: used(usage?.technicalScopes) }),
        description: "Scope documents for audits, tests and other work someone carries out on your systems",
        keywords: ["scope", "statement of work", "penetration test", "pen test", "rules of engagement", "audit scope", "technical scope", "ce plus", "red team"],
      },
      {
        // Questionnaires the organisation's customers send it, answered from the records (docs/CUSTOMER-QUESTIONNAIRES.md).
        id: "customer-questionnaires", label: "Customer questionnaires", to: "/customer-questionnaires",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.customerQuestionnaires, "Customer questionnaires"),
        deferred: defer({ advanced: true, used: used(usage?.customerQuestionnaires) }),
        description: "Answer the security questionnaires your customers send you, from your own records",
        keywords: ["security questionnaire", "customer questionnaire", "sig", "sig lite", "caiq", "vendor assessment", "due diligence", "rfp", "trust"],
      },
    ],
  };

  const risks: NavItem = {
    id: "risks-area", label: "Risk & suppliers", icon: Shield,
    tabs: [
      { id: "risk-register", label: "Risk register", to: "/risk-register", description: "Your risks and how you are treating them", keywords: ["risks", "treatment"] },
      { id: "tprm", label: "Suppliers", to: "/tprm", hidden: !addons.tprm, description: "Supplier risk, questionnaires and contracts", keywords: ["supplier risk", "tprm", "third party", "vendors"] },
      { id: "threat-intelligence", label: "Threats", to: "/threat-intelligence", deferred: defer({ advanced: true }), description: "Security advisories that affect your devices", keywords: ["threat intelligence", "advisories", "cve", "threats"] },
      {
        id: "vulnerabilities", label: "Vulnerabilities", to: "/vulnerabilities", badge: "vulnerabilities",
        deferred: defer({ relevant: used(usage?.vulnerabilities), used: used(usage?.vulnerabilities) || (counts?.vulnerabilities ?? 0) > 0 }),
        description: "What to patch first, with a deadline for each",
        keywords: ["vulnerability management", "vulnerability scanning", "nessus", "qualys", "openvas", "patching", "cve", "kev", "epss", "a.8.8"],
      },
      {
        id: "exposure", label: "Exposure", to: "/exposure",
        deferred: defer({ advanced: true, used: used(usage?.quantified) }),
        description: "What your risks could cost in a year, and against your insurance",
        keywords: ["exposure modelling", "quantified risk", "fair", "annual loss expectancy", "monte carlo", "scenario"],
      },
    ],
  };

  // Operational resilience - base tier, every client organisation (docs/OPERATIONAL-RESILIENCE.md).
  // Incidents are always offered: a notification clock can start any day. The rest appear once used.
  const resilienceAttention = (counts?.resilience ?? 0) > 0;
  const resilience: NavItem = {
    id: "resilience-area", label: "Resilience", icon: LifeBuoy, badge: "resilience",
    tabs: [
      {
        id: "resilience", label: "Overview", to: "/resilience", exact: true, badge: "resilience",
        deferred: defer({ advanced: true, used: resilienceAttention }),
        description: "What needs attention across your resilience registers",
        keywords: ["resilience overview", "operational resilience", "attention", "playbook", "ransomware"],
      },
      { id: "resilience-incidents", label: "Incidents", to: "/resilience/incidents", description: "Log an incident and see who must be told, and by when", keywords: ["cyber incidents", "incident", "breach", "notification", "jcsc", "ncsc", "regulator", "24 hours"] },
      {
        // Always offered beside Incidents: nobody should have to find the room in the middle of one.
        id: "resilience-rooms", label: "Response rooms", to: "/resilience/rooms",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.responseRooms, "Response rooms"),
        description: "Run an incident, a continuity invocation or an exercise live, and keep the offline crisis pack",
        keywords: ["response room", "war room", "incident response", "business continuity", "invoke", "situation report", "sitrep", "crisis pack", "major incident"],
      },
      {
        // Continuity: offered in Guided mode to anyone an essential-services regime could designate.
        id: "resilience-bia", label: "Business impact", to: "/resilience/bia", badge: "bia",
        deferred: defer({ advanced: !counts?.oesPossible, used: used(usage?.businessImpact) || (counts?.bia ?? 0) > 0 }),
        description: "How long each process can be down, and whether it can recover in time",
        keywords: ["business impact analysis", "bia", "continuity", "rto", "rpo", "mtpd", "disaster recovery", "a.5.30", "iso 22301"],
      },
      { id: "resilience-assets", label: "Assets", to: "/resilience/assets", deferred: defer({ advanced: true, used: used(usage?.assets) }), description: "Devices, systems and services you rely on", keywords: ["asset register", "assets", "inventory", "critical", "essential function", "contingency"] },
      {
        // Changes to systems and configuration, approved by someone else (docs/CHANGE-REGISTER.md).
        id: "resilience-changes", label: "Changes", to: "/changes",
        deferred: defer({ advanced: true, used: used(usage?.changes) }),
        description: "Changes to systems and configuration, approved, made and reviewed",
        keywords: ["change register", "change management", "change request", "emergency change", "cab", "a.8.32", "rollback"],
      },
      {
        // PCs running the WingGuard app report their Cyber Essentials findings here (docs/DEVICE-LINK.md).
        id: "resilience-devices", label: "Devices", to: "/devices",
        description: "PCs running the WingGuard app, their Cyber Essentials gaps and what they have done",
        keywords: ["devices", "wingguard", "pcs", "laptops", "endpoints", "cyber essentials", "device scanner", "block program", "restore point", "support access"],
      },
      { id: "resilience-exercises", label: "Exercises", to: "/resilience/exercises", deferred: defer({ relevant: used(usage?.exercises), used: used(usage?.exercises) }), description: "Tabletops, restore tests and phishing simulations", keywords: ["exercises & testing", "tabletop", "disaster recovery", "backup restore", "penetration test", "phishing simulation"] },
      { id: "resilience-training", label: "Training", to: "/resilience/training", deferred: defer({ relevant: used(usage?.training) || used(usage?.trainingAssignments), used: used(usage?.training) || used(usage?.trainingAssignments) }), description: "Who has done security awareness training", keywords: ["training & awareness", "training", "awareness", "phishing", "culture"] },
      { id: "resilience-insurance", label: "Insurance", to: "/resilience/insurance", deferred: defer({ relevant: used(usage?.insurance), used: used(usage?.insurance) }), description: "Your cyber insurance cover and its conditions", keywords: ["cyber insurance", "insurance", "insurer", "cover", "policy"] },
      { id: "dora", label: "DORA", to: "/dora", hidden: !addons.doraMonitoring, description: "ICT incidents, testing and providers under DORA", keywords: ["dora monitoring", "ict", "resilience", "incidents", "register of information"] },
    ],
  };

  // The ISO management-system registers (docs/MANAGEMENT-SYSTEM.md) sit with the corrective actions
  // they feed: one area for clauses 4–10. Each applies only with a standard that asks for it
  // (ISO 27001 / 9001 / 42001 / 45001 / 14001; aspects 14001 only, hazards 45001 only).
  const msStandards = (["iso27001", "iso9001", "iso42001", "iso45001", "iso14001"] as const).filter((f) => enrolled.has(f));
  const msRelevant = msStandards.length > 0;
  // A rehearsal is for a standard a certification body audits, run by audit:write or controls:write.
  const hasCertifiable = [...enrolled].some((f) => (CERTIFIABLE_FRAMEWORKS as readonly string[]).includes(f));
  const canRehearse = has("audit:write") || has("controls:write");
  const improvement: NavItem = {
    id: "improvement-area", label: "Management system", icon: Wrench, badge: "improvement",
    tabs: [
      {
        id: "management-system", label: "Overview", to: "/management-system", exact: true, badge: "managementSystem",
        deferred: defer({ relevant: msRelevant, used: (counts?.managementSystem ?? 0) > 0 }),
        description: "What needs doing on audits, reviews, objectives and the other management system records",
        keywords: ["management system", "clauses 4-10", "iso", "isms", "qms", "ems", "harmonised structure"],
      },
      {
        id: "ms-audits", label: "Internal audits", to: "/management-system/audits",
        deferred: defer({ relevant: msRelevant, used: used(usage?.msAudits) }),
        description: "The audit programme, each audit and its findings", keywords: ["internal audit", "audit programme", "9.2", "nonconformity", "findings"],
      },
      {
        // A rehearsal of a certification audit, private to the organisation (docs/MOCK-AUDIT.md).
        id: "mock-audit", label: "Mock audit", to: "/mock-audits",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.mockAudit, "Mock audit rehearsal"),
        hidden: !canRehearse,
        deferred: defer({ relevant: hasCertifiable, used: false }),
        description: "Rehearse a certification audit: the sample, what an auditor would ask for and the likely findings",
        keywords: ["mock audit", "rehearsal", "audit readiness", "dry run", "stage 2", "certification audit", "nonconformity"],
      },
      {
        id: "ms-reviews", label: "Management review", to: "/management-system/reviews",
        deferred: defer({ relevant: msRelevant, used: used(usage?.msReviews) }),
        description: "Top management's reviews, their inputs and decisions", keywords: ["management review", "9.3", "board review", "minutes"],
      },
      {
        id: "ms-objectives", label: "Objectives", to: "/management-system/objectives",
        deferred: defer({ relevant: msRelevant, used: used(usage?.msObjectives) }),
        description: "Measurable objectives, targets and results", keywords: ["objectives", "kpi", "6.2", "9.1", "measurement", "targets"],
      },
      {
        id: "ms-context", label: "Context", to: "/management-system/context",
        deferred: defer({ relevant: msRelevant, used: used(usage?.msContext) }),
        description: "Issues and interested parties with their requirements", keywords: ["context", "interested parties", "stakeholders", "4.1", "4.2", "pestle", "swot"],
      },
      {
        id: "ms-competence", label: "Competence", to: "/management-system/competence",
        deferred: defer({ relevant: msRelevant, used: used(usage?.msCompetence) }),
        description: "The competence each role needs and the evidence people hold it", keywords: ["competence", "7.2", "skills", "qualifications", "training matrix"],
      },
      {
        id: "ms-aspects", label: "Environmental aspects", to: "/management-system/aspects",
        deferred: defer({ relevant: enrolled.has("iso14001"), used: used(usage?.msAspects) }),
        description: "Aspects, impacts and significance (ISO 14001)", keywords: ["environmental aspects", "impacts", "significance", "6.1.2", "iso 14001"],
      },
      {
        id: "ms-hazards", label: "OH&S hazards", to: "/management-system/hazards",
        deferred: defer({ relevant: enrolled.has("iso45001"), used: used(usage?.msHazards) }),
        description: "Hazards and OH&S risk before and after controls (ISO 45001)", keywords: ["hazards", "health and safety", "risk assessment", "hierarchy of controls", "iso 45001"],
      },
      {
        id: "ms-obligations", label: "Compliance obligations", to: "/management-system/obligations",
        deferred: defer({ relevant: enrolled.has("iso14001") || enrolled.has("iso45001"), used: used(usage?.msObligations) }),
        description: "Legal and other requirements, and the evaluation of compliance", keywords: ["compliance obligations", "legal register", "evaluation of compliance", "6.1.3", "9.1.2", "permits"],
      },
      {
        id: "improvement", label: "Corrective actions", to: "/improvement", badge: "improvement",
        deferred: defer({ relevant: used(usage?.correctiveActions), used: used(usage?.correctiveActions) || (counts?.improvement ?? 0) > 0 }),
        description: "Fixes you have committed to, with owners and dates",
        keywords: ["actions", "capa", "programme", "remediation", "milestones", "overdue", "root cause"],
      },
      {
        id: "remediation", label: "Remediation", to: "/remediation", badge: "remediation",
        deferred: defer({ advanced: true, used: used(usage?.playbooks) || (counts?.remediation ?? 0) > 0 }),
        description: "Your rules for fixing things automatically, with approval for anything on a device",
        keywords: ["agentic remediation", "automation", "playbooks", "auto-remediation", "defender", "intune"],
      },
    ],
  };

  const security: NavItem = {
    id: "security-area", label: "Security & AI", icon: ShieldCheck, badge: "aiSystems",
    tabs: [
      { id: "estate", label: "Network zones", to: "/estate", deferred: defer({ relevant: used(usage?.networkZones), used: used(usage?.networkZones) }), description: "How your network and operational technology are separated", keywords: ["estate & segmentation", "zones", "purdue", "ot", "operational technology", "segmentation", "physical security", "import"] },
      {
        id: "hardening", label: "Hardening", to: "/hardening", hidden: !addons.hardening,
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.hardening, "Hardening Benchmarks"),
        description: "CIS benchmarks for your devices and services",
        keywords: ["cis hardening", "cis", "benchmarks", "baseline"],
      },
      {
        id: "ai-systems", label: "AI systems", to: "/ai-systems", badge: "aiSystems",
        deferred: defer({ relevant: enrolled.has("iso42001"), used: used(usage?.aiSystems) || (counts?.aiSystems ?? 0) > 0 }),
        description: "The AI tools you use and build, and the rules that apply",
        keywords: ["ai", "ai act", "shadow ai", "iso 42001", "regulatory watch", "impact assessment"],
      },
    ],
  };

  const reports: NavItem = {
    id: "reports-area", label: "Reports", icon: FileText,
    tabs: [
      {
        id: "executive-report", label: "Board report", to: "/executive-report", hidden: !canReport,
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.executiveReport, "Executive Report"),
        description: "A board-ready summary of where you stand",
        keywords: ["executive report", "board", "pdf", "summary"],
      },
      {
        id: "assurance-packs", label: "Assurance packs", to: "/assurance-sharing", hidden: !isAdmin,
        deferred: defer({ advanced: true, used: used(usage?.assurancePacks) }),
        description: "Share your security position with insurers, brokers and customers",
        keywords: ["external review", "insurer", "broker", "underwriter", "trust", "questionnaire", "share"],
      },
      {
        id: "auditor-feedback", label: "Auditor feedback", to: "/auditor-feedback", badge: "unresolvedFeedback",
        lock: addons.auditorAccess ? undefined : tierLock(tierLevel, CLIENT_MIN_TIER.auditorFeedback, "Auditor Feedback"),
        description: "Findings and evidence requests from your certification body",
        keywords: ["findings", "evidence requests", "auditor"],
      },
    ],
  };

  // Playbooks: the organisation's own playbooks, generated like its policies (docs/PLAYBOOKS.md).
  // Its own row at the owner's request — playbooks cut across incidents, suppliers, data
  // protection and risk, so no single task area holds them. The verbatim guidance stays in Help.
  const playbooksArea: NavItem = {
    id: "playbooks", label: "Playbooks", icon: BookOpenCheck, to: "/playbooks",
    description: "Your own playbooks, generated from your records beside the published guidance",
    keywords: ["playbook", "runbook", "procedure", "what to do", "incident response", "ransomware", "suppliers", "continuity"],
  };

  const work: NavSection = { id: "work", label: "Work", items: [playbooksArea, documents, risks, resilience, improvement, security, reports] };

  // ── Settings & help ─────────────────────────────────────────────────────
  const settings: NavItem = {
    id: "settings-area", label: "Settings", icon: Building2,
    tabs: [
      { id: "org-profile", label: "Organisation", to: "/org-profile", hidden: !isAdmin, description: "Your company profile, sector and locations", keywords: ["my organisation", "company", "profile", "legal obligations"] },
      { id: "team", label: "Team", to: "/team", hidden: !isAdmin, description: "Members, roles and invitations", keywords: ["my team", "members", "invite", "roles", "sign-in", "sign in policy", "single sign-on", "sso", "mfa", "authenticator", "two-factor"] },
      { id: "my-services", label: "Services & billing", to: "/my-services", hidden: !canBilling, description: "Your plan, standards and add-ons", keywords: ["my services", "tier", "billing", "add-ons", "standards", "language", "display language", "spend cap", "token budget"] },
      {
        id: "connections", label: "Connections", to: "/connections", hidden: !canIntegrate,
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.connections, "Connections"),
        activeMatch: ["/m365", "/google", "/storage"],
        description: "Microsoft 365, Google, cloud and storage",
        keywords: ["integrations", "microsoft 365", "google", "aws", "intune", "teams", "outlook", "calendar sync", "teams notifications", "digest"],
      },
      {
        id: "operating-parameters", label: "Operating parameters", to: "/operating-parameters", hidden: !isAdmin,
        description: "The deadlines and review cycles your registers run to, and the figures your policies set",
        keywords: ["operating parameters", "deadlines", "sla", "review cycle", "retention", "policy figures", "remediation deadline"],
      },
      { id: "sites", label: "Sites", to: "/sites", hidden: !isAdmin, deferred: defer({ advanced: true }), description: "Offices and locations", keywords: ["sites & locations", "sites", "offices", "locations", "countries", "multi-site"] },
      { id: "departments", label: "Departments & tags", to: "/departments", hidden: !isAdmin, deferred: defer({ advanced: true }), description: "Departments and the tags you file things under", keywords: ["departments", "tags"] },
      {
        id: "people", label: "Joiners & leavers", to: "/people", badge: "people", hidden: !canPeople,
        deferred: defer({ advanced: true, used: used(usage?.lifecycle) || (counts?.people ?? 0) > 0 }),
        description: "Onboarding and offboarding checklists that tick themselves",
        keywords: ["people lifecycle", "joiner mover leaver", "jml", "offboarding", "onboarding", "hris", "hr system", "a.6.5"],
      },
      {
        id: "access-reviews", label: "Access reviews", to: "/access-reviews", hidden: !canPeople,
        deferred: defer({ advanced: true, used: used(usage?.accessReviews) }),
        description: "Managers confirm who still needs each group and application",
        keywords: ["access review", "user access review", "recertification", "attestation", "entitlements", "a.5.18", "cc6"],
      },
      { id: "public-api", label: "API", to: "/public-api", hidden: !(canIntegrate && publicApiEnabled), deferred: defer({ advanced: true }), description: "API keys and webhooks", keywords: ["public api", "api keys", "webhooks", "developer"] },
      { id: "activity-log", label: "Activity log", to: "/activity-log", deferred: defer({ advanced: true }), description: "Who changed what, and when", keywords: ["audit log", "history"] },
    ],
  };

  const help: NavItem = {
    id: "help-area", label: "Help", icon: CircleHelp,
    tabs: [
      { id: "help", label: "Help & guide", to: "/help", exact: true, description: "How the platform works", keywords: ["help", "guide", "faq"] },
      { id: "help-playbooks", label: "Playbooks", to: "/help/playbooks", description: "Good practice for incidents, suppliers, continuity and more", keywords: ["playbook", "incident response", "ransomware", "third party risk", "business continuity", "best practice", "nist", "ncsc"] },
      { id: "help-library", label: "Library", to: "/help/library", description: "The NIST, NCSC, ENISA and EDPB guidance the playbooks quote", keywords: ["library", "guidance", "nist", "ncsc", "enisa", "edpb", "training material", "reference"] },
      {
        // Questions answered from the registers (backend lib/registerQuestions.ts): only records the member can open.
        id: "help-ask", label: "Ask your registers", to: "/help/ask",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.askRegisters, "Ask your registers"),
        description: "Ask a question in words and get an answer from your own registers, each record linked",
        keywords: ["ask", "question", "search registers", "which suppliers", "what is overdue", "query", "find records"],
      },
      { id: "consultancy-time", label: "Book a consultant", to: "/consultancy-time", hidden: !isAdmin, description: "Book time with your consultant", keywords: ["book consultancy time", "consultant", "booking", "hours"] },
    ],
  };

  const account: NavSection = { id: "account", items: [settings, help] };

  // A specialist role's sidebar shows its own job (nav/roleFocus.ts) — navigation, never access.
  return applyRoleFocus([home, standards, law, work, account], permissions)
    .filter((section) => section.items.some((item) => !item.hidden));
}

/** The "get set up" steps for org admins; null once every step is complete. Shown on What's next. */
export function buildSetupChecklist(setup: ClientNavCounts["setup"] | null | undefined): SetupChecklist | undefined {
  if (!setup) return undefined;
  const items = [
    { id: "sector", label: "Set your regulated sector", done: setup.sector, to: "/org-profile" },
    { id: "frameworks", label: "Choose a standard", done: setup.frameworks, to: "/my-services" },
    { id: "intake", label: "Tell us about your business", done: setup.intake, to: "/compliance-questions" },
    { id: "firstCheck", label: "Run your first compliance check", done: setup.firstCheck, to: "/compliance" },
    { id: "evidence", label: "Connect or upload evidence", done: setup.evidence, to: "/connections" },
    { id: "team", label: "Invite your team", done: setup.team, to: "/team" },
  ];
  if (items.every((i) => i.done)) return undefined;
  return { title: "Get set up", items };
}
